import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Logger from 'js-logger';
import _get from 'lodash/get';
import { Snackbar } from '@lifesize/clients.mui-components';
import LoadingAnimation from 'components/LoadingAnimation';
import { DefaultSettings, MainContext, radioButtonValues } from 'context';
import { RootState } from 'redux/rootReducer';
import { getExtensionInviteText } from 'utils/inviteUtils';
import { createMeeting, isDirectMeeting, isExistingMeeting, isOneTimeMeeting, isRoomSystem } from 'utils/meetingUtils';
import { createOrAppendItem } from 'utils/officeJS/schedule';
import { lsGet, LsKey } from 'utils/localStorageUtils';
import SignOut from 'components/SignOut';
import GetStarted from 'components/GetStarted';
import { ACCOUNT_STATUS, Session } from 'redux/checkInSlice';
import * as e from 'utils/constants/errors';
import { NO_OFFICE } from 'utils/constants/constants';
import { isMailBoxVersionSupported, MAILBOX_VERSION_1_5 } from 'utils/officeJS/outlookUtils';
import UnsupportedMailBox from 'components/UnsupportedMailbox';
import { updateInvitation } from 'redux/invitationSlice';
import { Routes } from 'layouts/Routes';
import _isEmpty from 'lodash/isEmpty';
interface Props {
  children: ReactNode;
}

export const MainProvider = ({ children }: Props) => {
  // setting initial state will help in Quick invite.
  const initialValues = getInitialValues();
  const [radioValue, setRadioValue] = useState(initialValues.radioValue);
  const [existingMeeting, setExistingMeeting] = useState(null);
  const [roomSystem, setRoomSystem] = useState(null);
  const [oneTimeName, setOneTimeName] = useState(initialValues.meetingName);
  const [oneTimeModerator, setOneTimeModerator] = useState(null);
  const [oneTimePasscode, setOneTimePasscode] = useState('');
  const [canSchedule, setCanSchedule] = useState(true);
  const [creatingAppointment, setCreatingAppointment] = useState(false);
  const [scheduleMeetingError, setScheduleMeetingError] = useState('');
  const [scheduleMeetingErrorDetails, setScheduleMeetingErrorDetails] = useState({});
  const loginState = useSelector((state: RootState) => state.logIn);
  const officeContext = useSelector((state: RootState) => state.officeContext);
  const snackBarState = useSelector((state: RootState) => state.snackBar);
  const accountFeatures = useSelector((state: RootState) => state.checkIn.account.features);
  const accountSettings = useSelector((state: RootState) => state.checkIn.account.settings);
  const accountStatus = useSelector((state: RootState) => state.checkIn.account.status);
  const session: Session = useSelector((state: RootState) => state.checkIn.session);
  const { extension: ownerExtension, displayName } = session;
  const defaultSettings: DefaultSettings = JSON.parse(lsGet(LsKey.Settings) || '{}');
  const { includeMessage, customMessage } = defaultSettings;
  const [mailBoxSupported, setMailBoxSupported] = useState(true);
  const invitation = useSelector((state: RootState) => state.invitation);
  const dispatch = useDispatch();
  const handleError = (e: any, additionalDetails: any) => {
    Logger.error(e);
    checkErrorMessage(e, additionalDetails);
    setCreatingAppointment(false);
  };

  function getInitialValues() {
    const pathName = _get(window, 'location.pathname');
    const settings = JSON.parse(lsGet(LsKey.Settings) || '{}');
    return (pathName.includes(Routes.MEET_NOW) || pathName.includes(Routes.SETTINGS)) && !_isEmpty(settings)
      ? { radioValue: settings.meetingType, meetingName: settings.meetingValue }
      : { radioValue: radioButtonValues.direct, meetingName: '' };
  }
  /*
    Safari doesn't like async...await function which will open a pop window.
    To get around it, async...await is replaced with good old fashioned promise.
  */
  const scheduleMeetingForExtension = ({ extension, meetingName }: any) => {
    setCreatingAppointment(true);
    getExtensionInviteText(extension)
      .then((meetingInvitation) => {
        if (includeMessage) {
          meetingInvitation.body = `${customMessage} \r\n ${meetingInvitation.body}`;
        }
        dispatch(updateInvitation({ ...meetingInvitation, meetingName, extension }));
        setCreatingAppointment(false);
      })
      .catch((e) => {
        handleError(e, { meetingName });
      });
  };

  const isOneTimeMeetingEnabled = () => _get(accountFeatures, 'oneTimeMeeting');

  const meetingsRequirePin = () => _get(accountSettings, 'requireMeetingPasscode');

  const generateRandomPasscode = () => {
    const randomizer = [];
    while (randomizer.length < 6) {
      randomizer.push(Math.floor(Math.random() * 10));
    }
    return randomizer.join('');
  };

  const checkErrorMessage = (error: any, additionalDetails: any) => {
    let errorFromMessage = e.GENERIC_MEETING_ERROR;
    let errorDetails = {};

    if (error.statusCode === e.STATUS_CODE_400) {
      errorDetails = { meetingName: additionalDetails.meetingName };
      if (error.errorDescription === e.MEETING_NAME_EXISTS) {
        errorFromMessage = e.MEETING_NAME_EXISTS;
      } else if (error.errorID === e.EXTENSION_NOT_FOUND) {
        errorFromMessage = e.EXTENSION_NOT_FOUND;
      }
    }

    setScheduleMeetingError(errorFromMessage);
    setScheduleMeetingErrorDetails(errorDetails);
  };

  const shouldUpdateInvitation = ({ extension }: any) => {
    return invitation.extension !== extension;
  };

  const scheduleMeeting = async () => {
    switch (radioValue) {
      case radioButtonValues.direct:
        if (shouldUpdateInvitation({ extension: ownerExtension })) {
          await scheduleMeetingForExtension({ extension: ownerExtension, meetingName: oneTimeName || displayName});
        }
        break;
      case radioButtonValues.find:
        const extension = _get(existingMeeting, 'extension') || '';
        const meetingName = _get(existingMeeting, 'displayName') || '';
        if (shouldUpdateInvitation({ extension })) {
          await scheduleMeetingForExtension({ extension, meetingName });
        }
        break;
      case radioButtonValues.findRoom:
        const roomSystemExtension = _get(roomSystem, 'extension') || '';
        const roomSystemName = _get(roomSystem, 'displayName') || '';
        if (shouldUpdateInvitation({ extension: roomSystemExtension })) {
          await scheduleMeetingForExtension({ extension: roomSystemExtension, meetingName: roomSystemName });
        }
        break;
      case radioButtonValues.oneTime:
        try {
          setCreatingAppointment(true);
          if (!isOneTimeMeetingEnabled()) {
            throw new Error(JSON.stringify({ statusCode: 400, errorDescription: 'oneTimeDisabled' }));
          }

          const pinRequired = meetingsRequirePin();
          let pin = oneTimePasscode;
          if (pinRequired && !pin?.length) {
            pin = generateRandomPasscode();
          }

          const newMeeting = await createMeeting({
            meetingType: radioValue as string,
            oneTimeName,
            oneTimeModerator,
            oneTimePasscode: pin,
            ownerExtension
          });
          const newMeetingExtension = _get(newMeeting, 'extension');
          const meetingInvitation = await getExtensionInviteText(newMeetingExtension);
          // This function will only run if it is loaded in Outlook.
          if (includeMessage) {
            meetingInvitation.body = `${customMessage}\r\n${meetingInvitation.body}`;
          }
          dispatch(
            updateInvitation({ ...meetingInvitation, meetingName: oneTimeName, extension: newMeetingExtension })
          );
          setCreatingAppointment(false);
        } catch (e) {
          Logger.error(e);
          checkErrorMessage(e, { meetingName: oneTimeName });
          setCreatingAppointment(false);
        }
        break;
    }
  };

  useEffect(() => {
    if (isExistingMeeting(radioValue)) {
      setCanSchedule(!!existingMeeting);
      existingMeeting && scheduleMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingMeeting]);

  useEffect(() => {
    if (isDirectMeeting(radioValue)) {
      !canSchedule && setCanSchedule(true);
      ownerExtension && scheduleMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioValue, ownerExtension, oneTimeName]);

  useEffect(() => {
    if (isOneTimeMeeting(radioValue) && oneTimeModerator) {
      setCanSchedule(!!oneTimeName && !!oneTimeModerator);
      oneTimeModerator && scheduleMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneTimeName, oneTimeModerator]);

  useEffect(() => {
    if (isRoomSystem(radioValue)) {
      setCanSchedule(!!roomSystem);
      roomSystem && scheduleMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomSystem]);

  useEffect(() => {
    const resetMeetingTypeForm = () => {
      setRadioValue(radioButtonValues.direct);
      setOneTimeModerator(null);
      resetForm();
    };
    if (loginState.isLoggedOut) resetMeetingTypeForm();
  }, [loginState.isLoggedOut]);

  useEffect(() => {
    // this side effect should only run & add class to root element after launching outside of outlook.
    if (NO_OFFICE) {
      const rootElem = document.getElementById('root');
      rootElem && rootElem.classList.add('noOffice');
    }
  }, [officeContext]);

  const resetForm = () => {
    setExistingMeeting(null);
    setRoomSystem(null);
    setOneTimeName('');
    setOneTimeModerator(null);
    setOneTimePasscode('');
    setCanSchedule(false);
    setScheduleMeetingError('');
  };

  const isOfficeForPC = officeContext.platform === Office.PlatformType.PC;

  const displayAppointmentForm = () => {
    try {
      createOrAppendItem(invitation, invitation.extension, officeContext.isOfficeWeb, isOfficeForPC);
      setMailBoxSupported(isMailBoxVersionSupported(MAILBOX_VERSION_1_5));
    } catch (e) {
      Logger.error(e);
    }
  };

  if (!mailBoxSupported) {
    return <UnsupportedMailBox />;
  }

  if (accountStatus !== ACCOUNT_STATUS.UNAUTHORIZED) {
    if (loginState.wampConnectError) {
      return <GetStarted />;
    }

    if (loginState.isLoggedOut) {
      return <SignOut />;
    }

    if (!loginState.isLoggedIn || loginState.isLoggingOut) {
      return <LoadingAnimation />;
    }
  }

  return (
    <>
      {snackBarState.message && <Snackbar message={snackBarState.message} />}
      <MainContext.Provider
        value={{
          canSchedule,
          creatingAppointment,
          displayAppointmentForm,
          existingMeeting,
          mailBoxSupported,
          oneTimeModerator,
          oneTimeName,
          oneTimePasscode,
          radioValue,
          resetForm,
          scheduleMeeting,
          scheduleMeetingError,
          scheduleMeetingErrorDetails,
          setCanSchedule,
          setExistingMeeting,
          setRoomSystem,
          roomSystem,
          setOneTimeModerator,
          setOneTimeName,
          setOneTimePasscode,
          setRadioValue,
          setScheduleMeetingError
        }}
      >
        {children}
      </MainContext.Provider>
    </>
  );
};

export const MainConsumer = MainContext.Consumer;
